import React, { useState, useEffect } from 'react';
import { getItemHistory } from '../api/inventory';
import { useNavigation } from '../context/NavigationContext';

const STATUS_COLOURS = {
  in_bin:"bg-green-100 text-green-700", in_mini_pack:"bg-green-100 text-green-700",
  in_full_pack:"bg-blue-100 text-blue-700", in_transit:"bg-blue-100 text-blue-700",
  inbounded:"bg-yellow-100 text-yellow-700", pre_inbound:"bg-yellow-50 text-yellow-500",
  in_ironing_queue:"bg-orange-100 text-orange-700", in_repair_queue:"bg-orange-100 text-orange-600",
  reversed:"bg-purple-100 text-purple-700", sold:"bg-gray-100 text-gray-400",
  damaged:"bg-red-100 text-red-600", missing:"bg-red-100 text-red-600",
  returned_to_brand:"bg-gray-100 text-gray-400", picked_from_customer:"bg-purple-100 text-purple-600",
  liquidated:"bg-gray-50 text-gray-400",
};

function groupEvents(events) {
  const groups = [];
  let cur = null;
  for (const e of events) {
    const key = `${e.event_type}|${e.created_at?.slice(0,19)}`;
    if (!cur || cur.key !== key) {
      cur = { key, event_type:e.event_type, created_at:e.created_at,
              performed_by:e.performed_by, notes:e.notes, fields:[] };
      groups.push(cur);
    }
    cur.fields.push(e);
  }
  return groups;
}

export default function ItemHistory() {
  const { navData } = useNavigation();

  const [query, setQuery]       = useState("");
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [eventType, setEventType] = useState("");
  const [dateFrom, setDateFrom]   = useState("");
  const [dateTo, setDateTo]       = useState("");

  async function doSearch(q, overrides = {}) {
    const target = q ?? query;
    if (!target.trim()) return;
    setLoading(true); setError(null); setData(null);
    try {
      const d = await getItemHistory(target.trim(), {
        event_type: overrides.eventType ?? eventType,
        date_from:  overrides.dateFrom  ?? dateFrom,
        date_to:    overrides.dateTo    ?? dateTo,
      });
      setData(d);
    } catch (e) {
      setError(e.response?.status === 404 ? "Item not found in the system." : (e.response?.data?.detail || "Something went wrong."));
    } finally { setLoading(false); }
  }

  // Auto-search when navigated from InventoryBrowser
  useEffect(() => {
    if (navData?.query) {
      setQuery(navData.query);
      doSearch(navData.query);
    }
  }, [navData?.query]);

  const eventTypes = data ? [...new Set(data.events.map(e => e.event_type))].sort() : [];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-xl font-black text-gray-800 mb-1">Item History</h1>
      <p className="text-sm text-gray-400 mb-6">Enter a Zoddle barcode or item UUID to view the full change history.</p>

      <div className="flex gap-3 mb-6">
        <input type="text" value={query} onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key==="Enter" && doSearch(query)}
          placeholder="ZOD12345678 or item UUID…"
          className="flex-1 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-zoddle-green font-mono" />
        <button onClick={() => doSearch(query)} disabled={loading || !query.trim()}
          className="bg-zoddle-green text-white font-bold text-sm px-5 py-2.5 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
          {loading ? "Loading…" : "Search"}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-3 text-sm text-zoddle-pink mb-4">{error}</div>
      )}

      {data && (
        <>
          {/* Item card */}
          <div className="bg-white border border-gray-100 rounded-xl p-4 mb-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-base font-bold text-gray-800">{data.item.zoddle_barcode}</p>
                <p className="text-sm text-gray-600 mt-0.5">{data.item.design_name} · {data.item.size} · {data.item.brand_name}</p>
                {data.item.design_code && <p className="text-xs text-gray-400 mt-0.5">Design {data.item.design_code}</p>}
              </div>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_COLOURS[data.item.current_status]||"bg-gray-100 text-gray-500"}`}>
                {data.item.current_status?.replace(/_/g," ")}
              </span>
            </div>
            <div className="flex gap-4 mt-3 text-xs text-gray-500">
              <span>₹{data.item.zoddle_price}</span>
              <span>·</span><span>MRP ₹{data.item.mrp}</span>
              <span>·</span><span>{data.item.current_condition}</span>
              {data.item.current_location_type && <><span>·</span><span>{data.item.current_location_type}</span></>}
            </div>
          </div>

          {/* Filter bar */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <p className="text-xs font-semibold text-gray-500">
              {data.total_events} event{data.total_events!==1?"s":""}
            </p>
            <div className="flex-1" />
            <select value={eventType}
              onChange={e => { setEventType(e.target.value); doSearch(query, { eventType:e.target.value }); }}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-700 bg-white focus:outline-none focus:border-zoddle-green">
              <option value="">All event types</option>
              {eventTypes.map(t => <option key={t} value={t}>{t.replace(/_/g," ")}</option>)}
            </select>
            <input type="date" value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); doSearch(query, { dateFrom:e.target.value }); }}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-700 focus:outline-none focus:border-zoddle-green" />
            <span className="text-xs text-gray-400">to</span>
            <input type="date" value={dateTo}
              onChange={e => { setDateTo(e.target.value); doSearch(query, { dateTo:e.target.value }); }}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-700 focus:outline-none focus:border-zoddle-green" />
            {(eventType||dateFrom||dateTo) && (
              <button onClick={() => { setEventType(""); setDateFrom(""); setDateTo(""); doSearch(query, {eventType:"",dateFrom:"",dateTo:""}); }}
                className="text-xs text-gray-400 hover:text-zoddle-pink">Clear</button>
            )}
          </div>

          {/* Events */}
          {data.events.length === 0 ? (
            <div className="bg-white border border-gray-100 rounded-xl p-8 text-center">
              <p className="text-sm text-gray-400">{(eventType || dateFrom || dateTo) ? "No events match the current filters." : "No changes on record for this item yet."}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {groupEvents(data.events).map((group, gi) => (
                <div key={gi} className="bg-white border border-gray-100 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="text-xs font-bold text-gray-800 bg-gray-100 px-2 py-0.5 rounded">
                      {group.event_type.replace(/_/g," ")}
                    </span>
                    <span className="text-xs text-gray-400">{group.created_at?.slice(0,16).replace("T"," ")}</span>
                    {group.performed_by && <span className="text-xs text-gray-400">· {group.performed_by}</span>}
                    {group.notes && <span className="text-xs text-gray-400 italic">· {group.notes}</span>}
                  </div>
                  <div className="space-y-1">
                    {group.fields.map((e, fi) => (
                      <div key={fi} className="flex items-center gap-3 text-xs">
                        <span className="text-gray-400 w-28 shrink-0">{e.field_name}</span>
                        {e.old_value ? <span className="text-gray-400 line-through">{e.old_value}</span>
                                      : <span className="text-gray-300">—</span>}
                        <span className="text-gray-400">→</span>
                        <span className="text-gray-800 font-medium">{e.new_value ?? "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
